<template>
	<AppLayout
		:title="event.title"
		:subtitle="event.subtitle"
		:return-url="route('events.index')"
		:return-label="trans('events.title')"
	>
		<template #actions>
			<Link v-if="enrolled && enrollment.is_pending" :as="PrimaryButton" :color="Color.Yellow" :href="route('events.enrollments.show', enrollment.id)">
				<i class="fa-solid fa-exclamation-triangle mr-2"/>
				{{ trans('events.pending') }}
			</Link>
			<Link v-else-if="enrolled" :as="PrimaryButton" :color="Color.Green" :href="route('events.enrollments.show', enrollment.id)">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('events.enrolled') }}
			</Link>
			<Link v-else-if="enrollmentIsAllowed && registrationStages.length > 0" :as="PrimaryButton" :color="Color.Blue" data-testid="enroll-button" :href="route('events.enrollments.create', event.id)">
				<i class="fa-solid fa-ticket mr-2"/>
				{{ trans('events.enroll') }}
			</Link>
		</template>
		<div class="grid grid-cols-1 lg:grid-cols-10 xl:grid-cols-11 gap-5">
			<div class="flex items-start justify-center lg:col-span-4">
				<div class="w-72 lg:w-full flex flex-col gap-3 sticky top-16">
					<Card :title="event.title" :subtitle="event.subtitle" :image="event.image" has-image>
						<LabeledObject>
							<template #label>{{ trans('account.type.title') }}</template>
							<p>{{ userType }}</p>
						</LabeledObject>
						<List>
							<ListItem
								v-for="stage in registrationStages"
								:title="trans('events.stages.registration.deadline', {date: dayjs(stage.ends_at).format(trans('general.dayjs.date'))})"
								:subtitle="`${trans('events.price_from')} $${formatNumber(stage.lowest_price, 2, trans('general.number.decimal'), trans('general.number.thousands'))}`"
							>
								<template #accessory>
									<div v-if="stage.early_bird" class="text-gray-500">
										<i class="fa-solid fa-dove mr-2"/>
										{{ trans('events.stages.registration.early_bird') }}
									</div>
								</template>
							</ListItem>
						</List>
					</Card>
					<Card v-if="event.sponsors.length > 0" :title="trans('events.sponsors.title')">
						<img v-for="sponsor in event.sponsors" :src="storage(sponsor.file)" :alt="sponsor.name" class="w-full">
					</Card>
				</div>
			</div>
			<div class="lg:col-span-6 xl:col-span-7 flex flex-col gap-5">
				<Card v-if="event.description" class="gap-0">
					<article class="wysiwyg-content" v-html="event.description"/>
				</Card>
				<img v-if="event.schedule_file" :src="storage(event.schedule_file)" class="w-full" :alt="trans('events.schedule.title')">
			</div>
		</div>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/Cards/Card.vue'
import List from '@/Components/Lists/List.vue'
import {type Event} from '@/Types/Events/Event'
import {formatNumber, storage} from '@/helpers'
import AppLayout from '@/Layouts/AppLayout.vue'
import ListItem from '@/Components/Lists/ListItem.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'
import {type RegistrationStage} from '@/Types/Events/Stages/RegistrationStage'
import LabeledObject from '@/Components/LabeledObject.vue'

const props = defineProps<{
	event:Event,
	pending:boolean,
	userType:string,
	enrolled:boolean,
	enrollment:Enrollment,
	enrollmentIsAllowed:boolean,
	registrationStages:Array<RegistrationStage>,
}>()

const event:Ref<Event> = ref(props.event)
const enrolled:Ref<boolean> = ref(props.enrolled)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const enrollmentIsAllowed:Ref<boolean> = ref(props.enrollmentIsAllowed)
const registrationStages:Ref<Array<RegistrationStage>> = ref(props.registrationStages)
</script>